import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AgentService } from '@/services/agent/core';
import { DocumentService } from '@/services/api/DocumentService';
import { Agent } from '@/types/agent/agent';
import { DocumentWithMetadata } from '@/types/knowledge/knowledge-base';

interface UseKnowledgeBaseToggleOptions {
  onSuccess?: () => void;
  showToasts?: boolean;
}

export const useKnowledgeBaseToggle = (options: UseKnowledgeBaseToggleOptions = {}) => {
  const { showToasts = true } = options;
  const queryClient = useQueryClient();

  const buildUpdatedKnowledgeBase = (
    existing: any[],
    docs: DocumentWithMetadata | DocumentWithMetadata[],
    isAdding: boolean,
  ) => {
    const docArray = Array.isArray(docs) ? docs : [docs];

    if (isAdding) {
      const newDocs = docArray
        .filter((doc) => doc.el_doc_id && !existing.some((kb: any) => kb.id === doc.el_doc_id))
        .map((doc) => ({ id: doc.el_doc_id!, type: "text" as const, name: doc.name }));
      return [...existing, ...newDocs];
    } else {
      const docIds = new Set(docArray.filter((doc) => doc.el_doc_id).map((doc) => doc.el_doc_id));
      return existing.filter((kb: any) => !docIds.has(kb.id));
    }
  };

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["agent"] });
    queryClient.invalidateQueries({ queryKey: ["agents"] });
    queryClient.invalidateQueries({ queryKey: ["user-agents"] });
    queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
    queryClient.invalidateQueries({ queryKey: ["documents"] });
    queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
  }, [queryClient]);

  const toggleDocuments = useCallback(
    async (
      agent: Agent,
      docs: DocumentWithMetadata | DocumentWithMetadata[],
      isAdding: boolean,
    ) => {
      let docArray = Array.isArray(docs) ? docs : [docs];

      try {
        if (isAdding && agent.user_id) {
          const results = await Promise.all(
            docArray.map(async (doc) => {
              if (doc.user_id && doc.user_id !== agent.user_id) {
                // Clone with agentId so the server handles ElevenLabs attachment too
                const cloned = await DocumentService.cloneDocument(doc.id, doc.name, agent.user_id, agent.id);
                return { doc: cloned ?? doc, wasCloned: !!cloned };
              }
              return { doc, wasCloned: false };
            }),
          );
          // Docs that were cloned with agentId are already attached server-side — exclude from manual update
          docArray = results.filter(({ wasCloned }) => !wasCloned).map(({ doc }) => doc);
          // But if all docs were cloned, we're done after invalidation
          if (docArray.length === 0) {
            invalidateQueries();
            if (showToasts) toast.success("Document linked to genie");
            options.onSuccess?.();
            return;
          }
        }

        const agentSettings =
          agent.settings ||
          (await AgentService.getAgentDetails(agent.id).then((res) => res.settings || {}));
        const existing = agentSettings.conversation_config?.agent?.prompt?.knowledge_base || [];
        const updated = buildUpdatedKnowledgeBase(existing, docArray, isAdding);

        console.log("kb", { existing, updated, isAdding });

        await AgentService.update(agent, {
          settings: {
            ...(agent.settings || {}),
            conversation_config: {
              ...(agent.settings?.conversation_config || {}),
              agent: {
                ...(agent.settings?.conversation_config?.agent || {}),
                prompt: {
                  ...(agent.settings?.conversation_config?.agent?.prompt || {}),
                  knowledge_base: updated,
                },
              },
            },
          },
        });

        invalidateQueries();

        if (showToasts) {
          if (docArray.length === 1) {
            toast.success(isAdding ? "Document linked to genie" : "Document unlinked from genie");
          } else {
            toast.success(
              isAdding
                ? `Successfully linked ${docArray.length} documents to genie`
                : `Successfully unlinked ${docArray.length} documents from genie`,
            );
          }
        }

        options.onSuccess?.();
      } catch (error) {
        console.error("Failed to update knowledge base:", error);
        if (showToasts) {
          toast.error("Failed to update knowledge base");
        }
        throw error;
      }
    },
    [invalidateQueries, showToasts, options],
  );

  return { toggleDocuments };
};
